import React, { useState } from 'react';
import { TextVariant } from '@naf/text';
import { CarMeta } from '@appTypes/carModelType';
import BlockContent from '@components/block-content/BlockContent';
import { ElevationChart } from '@components/SectionBlocks/blocks/Charts/ElevationChart';
import { CombinedResultCard } from '@components/SectionBlocks/Testshowcase/CombinedResultCard';
import { useElPrixRouteData } from '@hooks/useElPrixRouteData';
import { formattedPrice } from '@lib/formattedPrice';
import useSelector from '@redux/typedHooks';
import { BlockContentWrapper } from './BlockContentWrapper';
import { DataTable } from './DataTable';
import { VehicleVariantRadio } from './VehicleVariantRadio';
import { TestData } from './RangeTests.utils';
import * as S from './RangeTests.styles';

interface RangeTestContentProps {
  slug: string;
  tests: Record<string, TestData>;
  sanityImages: CarMeta['cloudinaryImages'];
}

export const RangeTestContent = ({ tests, sanityImages, slug }: RangeTestContentProps) => {
  const [selectedVariantId, setSelectedVariantId] = useState(Object.keys(tests)[0]);
  const selectedTest = tests[selectedVariantId];
  const vehicleRefId = (selectedTest?.vehicleModelReference as any)?._id;
  const variant = useSelector((state) =>
    state.cars.mappedData[slug].data.vehicleVariants.find((candidate) => candidate.id === vehicleRefId),
  );
  const {
    context,
    vehicleModelReference,
    statedRangeKm,
    measuredRangeKm,
    statedConsumptionKWhper100km,
    rangeTestSummary,
    rangeTestResultSummary,
    batteryDrainageSummary,
    batteryCapacity,
  } = selectedTest;
  const { season, testDate, isPartOfSeason } = context;
  const routepoints = isPartOfSeason ? context.routepoints : [];
  const elevation = isPartOfSeason ? context.altitudeProfile : [];
  const { pointsOfInterest } = useElPrixRouteData({ routepoints, elevation });
  const flyoutEntries = [
    { label: 'Pris', value: formattedPrice(vehicleModelReference.price ?? variant?.priceNOK ?? 0), unit: 'kr' },
    { label: 'Chassis', value: variant?.chassis },
    { label: 'Egenvekt uten fører', value: variant?.vehicleDimensions?.weight_kg, unit: 'kg' },
    { label: 'Drivhjul', value: variant?.driveTrain.type },
    { label: 'Hestekrefter', value: variant?.engineDetails[0].power_BHP },
    { label: 'Netto batteripakke', value: batteryCapacity ?? variant?.batteryDetails?.capacity_kWh, unit: 'kWh' },
    {
      label: 'Forbruk / 100 km (WLTP)',
      value: statedConsumptionKWhper100km ?? variant?.electricVehicleDetails.electricConsumptionWLTP_kWhper100km,
      unit: 'kWh',
      excludeField: 'electricConsumptionWLTP_kWhper100km',
    },
    {
      label: 'Rekkevidde (WLTP)',
      value: statedRangeKm || variant?.electricVehicleDetails.rangeWLTP_km,
      unit: 'km',
      excludeField: 'rangeWLTP_km',
    },
  ];

  return (
    <>
      {Object.keys(tests).length > 1 && (
        <VehicleVariantRadio
          name={`rangeTest_${season}_${testDate}`}
          tests={Object.values(tests)}
          selected={selectedVariantId}
          setSelected={setSelectedVariantId}
          sanityImages={sanityImages}
        />
      )}
      <BlockContentWrapper>
        <BlockContent value={rangeTestSummary} />
      </BlockContentWrapper>
      {(statedConsumptionKWhper100km || statedRangeKm) && (
        <CombinedResultCard
          variant="result"
          context={{ season, testDate, tests: { range: true, consumption: true, chargeTime: false, maxEffect: false } }}
          data={{
            testData: {
              chargeTest: null,
              image: sanityImages.find(
                (image) => image?.variant?.id && image.variant.id === vehicleModelReference.vehicleId,
              ),
              rangeTest: selectedTest,
            },
            ...vehicleModelReference,
          }}
        />
      )}
      {isPartOfSeason && elevation.length > 0 && (
        <>
          <S.ElevationChartContainer>
            <ElevationChart
              display="range"
              season={season}
              testDate={testDate}
              altitudes={elevation}
              pointsOfInterest={pointsOfInterest}
              rangeData={{ measured: measuredRangeKm, stated: statedRangeKm }}
            />
          </S.ElevationChartContainer>
          <S.Caption as="figcaption">
            Grafen viser høydeprofilen av testruten, og hvor bilen stanset i forhold til oppgitt WLTP.
          </S.Caption>
        </>
      )}
      {rangeTestResultSummary && (
        <>
          <S.ContentHeader variant={TextVariant.Header2}>Testresultater</S.ContentHeader>
          <BlockContentWrapper>
            <BlockContent value={rangeTestResultSummary} />
          </BlockContentWrapper>
        </>
      )}
      {batteryDrainageSummary && (
        <>
          <S.ContentHeader variant={TextVariant.Header2}>Slik oppleves det å kjøre tom for strøm</S.ContentHeader>
          <BlockContentWrapper>
            <BlockContent value={batteryDrainageSummary} />
          </BlockContentWrapper>
        </>
      )}
      {variant && (
        <DataTable
          tableTitle="Om testbilen"
          entries={flyoutEntries}
          flyoutData={{
            ...variant,
            vehicleVariantSpecification: {
              ...variant.vehicleVariantSpecification,
              general: {
                ...variant.vehicleVariantSpecification?.general,
                priceNOK: vehicleModelReference.price || variant.priceNOK,
              },
              electricAndEmissionDetails: {
                ...variant.vehicleVariantSpecification?.electricAndEmissionDetails,
                rangeWLTP_km: statedRangeKm || variant.electricVehicleDetails.rangeWLTP_km,
                electricConsumptionWLTP_kWhper100km:
                  statedConsumptionKWhper100km || variant.electricVehicleDetails.electricConsumptionWLTP_kWhper100km,
              },
            },
            exceptions: flyoutEntries
              .filter((entry) => entry.value)
              .map(({ excludeField, label }) => excludeField ?? label),
          }}
        />
      )}
    </>
  );
};
